if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.variantChangeUnsubscriber = undefined;

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        this.setupViewerNavigation();

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupViewerNavigation() {
        if (this.dataset.desktopLayout !== 'slider' || !this.elements.viewer) return;

        this.elements.viewer.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();

              const visibleMedia = this.getMediaItems().filter((item) => !item.hidden);
              if (visibleMedia.length <= 1) return;

              const activeIndex = visibleMedia.findIndex((item) => item.classList.contains('is-active'));
              const direction = button.name === 'next' ? 1 : -1;
              const nextIndex = (Math.max(activeIndex, 0) + direction + visibleMedia.length) % visibleMedia.length;
              this.setActiveMedia(visibleMedia[nextIndex].dataset.mediaId, false);
            },
            { capture: true }
          );
        });
      }

      connectedCallback() {
        if (!this.dataset.colorOptionPosition) return;

        if (
          !this.variantChangeUnsubscriber &&
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }

        this.filterMediaByColor(this.dataset.selectedColor);
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = undefined;
      }

      onVariantChange(event) {
        const eventSectionId = event?.data?.sectionId;
        if (eventSectionId && eventSectionId !== this.dataset.sectionId) return;

        const variant = event?.data?.variant;
        if (!variant) return;

        const optionPosition = Number(this.dataset.colorOptionPosition);
        const color = variant[`option${optionPosition}`] || variant.options?.[optionPosition - 1];
        const featuredMediaId = variant.featured_media?.id
          ? `${this.dataset.sectionId}-${variant.featured_media.id}`
          : null;

        this.dataset.selectedColor = color || '';
        this.filterMediaByColor(color, featuredMediaId);
      }

      normalizeColor(value) {
        return (value || '').trim().toLocaleLowerCase();
      }

      getMediaItems() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]'));
      }

      filterMediaByColor(color, preferredMediaId = null) {
        const selectedColor = this.normalizeColor(color);
        if (!selectedColor || !this.elements.viewer) return;

        const mediaItems = this.getMediaItems();
        const hasColorMatches = mediaItems.some(
          (item) => this.normalizeColor(item.dataset.mediaColor) === selectedColor
        );

        // Once this color has tagged media, show only exact matches. Blank or
        // unrelated alt text must not leak into the mobile swipe gallery.
        mediaItems.forEach((item) => {
          const mediaColor = this.normalizeColor(item.dataset.mediaColor);
          const shouldShow = !hasColorMatches || mediaColor === selectedColor;
          item.hidden = !shouldShow;
          item.classList.toggle('media-gallery__item--filtered', !shouldShow);
        });

        const visibleMedia = mediaItems.filter((item) => !item.hidden);
        const visibleMediaIds = new Set(visibleMedia.map((item) => item.dataset.mediaId));

        if (this.elements.thumbnails) {
          let visiblePosition = 0;
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
            thumbnail.hidden = !visibleMediaIds.has(thumbnail.dataset.target);
            thumbnail.classList.toggle('media-gallery__item--filtered', thumbnail.hidden);
            if (!thumbnail.hidden) {
              visiblePosition += 1;
              thumbnail.dataset.mediaPosition = visiblePosition;
            }
          });
        }

        const preferredMedia = preferredMediaId
          ? visibleMedia.find((item) => item.dataset.mediaId === preferredMediaId)
          : null;
        const firstColorMedia = visibleMedia.find(
          (item) => this.normalizeColor(item.dataset.mediaColor) === selectedColor
        );
        const activeMedia = preferredMedia || firstColorMedia || visibleMedia[0];

        if (activeMedia) this.setActiveMedia(activeMedia.dataset.mediaId, false);

        requestAnimationFrame(() => {
          this.elements.viewer?.resetPages?.();
          this.elements.thumbnails?.resetPages?.();

          const counterCurrent = this.elements.viewer?.querySelector('.slider-counter--current');
          const counterTotal = this.elements.viewer?.querySelector('.slider-counter--total');
          const previousButton = this.elements.viewer?.querySelector('button[name="previous"]');
          const nextButton = this.elements.viewer?.querySelector('button[name="next"]');
          const activeIndex = Math.max(visibleMedia.indexOf(activeMedia), 0);
          if (counterCurrent) counterCurrent.textContent = visibleMedia.length ? activeIndex + 1 : 0;
          if (counterTotal) counterTotal.textContent = visibleMedia.length;
          if (visibleMedia.length <= 1) {
            previousButton?.setAttribute('disabled', 'disabled');
            nextButton?.setAttribute('disabled', 'disabled');
          } else if (this.dataset.desktopLayout === 'slider') {
            previousButton?.removeAttribute('disabled');
            nextButton?.removeAttribute('disabled');
          }
        });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        if (!mediaId || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const mediaItems = this.getMediaItems();
        const activeMedia =
          mediaItems.find((item) => item.dataset.mediaId === mediaId && !item.hidden) ||
          mediaItems.find((item) => !item.hidden);
        if (!activeMedia) return;

        mediaItems.forEach((element) => element.classList.remove('is-active'));
        activeMedia.hidden = false;
        activeMedia.classList.remove('scroll-trigger--offscreen');
        activeMedia.classList.remove('animate--fade-in');
        activeMedia.classList.add('is-active');

        const visibleMedia = mediaItems.filter((item) => !item.hidden);
        const counterCurrent = this.elements.viewer.querySelector('.slider-counter--current');
        const counterTotal = this.elements.viewer.querySelector('.slider-counter--total');
        const activeIndex = visibleMedia.indexOf(activeMedia);
        if (counterCurrent && activeIndex >= 0) counterCurrent.textContent = activeIndex + 1;
        if (counterTotal) counterTotal.textContent = visibleMedia.length;

        if (prepend) {
          if (activeMedia.parentElement.firstElementChild !== activeMedia) activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstElementChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          this.elements.viewer.resetPages?.();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);
        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
